import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {Message} from 'primeng/message';
import {BadgeListComponent} from '../../list/badge-list/badge-list.component';
import {BadgeListItem, BadgeListItemToggle} from '../../list/badge-list/badge-list.types';
import {AttributeFulfillmentInputComponent} from '../fulfillment-input/attribute-fulfillment-input.component';
import {AttributeValueChange, SubcriteriaAttribute} from '../provider-questionnaire.types';
import {CommentModalButtonModelComponent} from '../../dialog/comment-modal-button/comment-modal-button-model/comment-modal-button-model.component';
import {TemplateSuggestionStore} from '../suggestion-review/template-suggestion.store';
import {SuggestionStatusTagComponent} from '../suggestion-review/suggestion-status-tag/suggestion-status-tag.component';
import {TemplateSuggestionReviewComponent} from '../suggestion-review/template-suggestion-review/template-suggestion-review.component';

@Component({
  selector: 'app-questionnaire-tab-content',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    Button,
    Message,
    BadgeListComponent,
    AttributeFulfillmentInputComponent,
    CommentModalButtonModelComponent,
    SuggestionStatusTagComponent,
    TemplateSuggestionReviewComponent,
  ],
  templateUrl: './questionnaire-tab-content.component.html',
})
export class QuestionnaireTabContentComponent {
  private readonly suggestionStore = inject(TemplateSuggestionStore);

  attributes = input.required<SubcriteriaAttribute[]>();
  badgeListItems = input.required<BadgeListItem[]>();
  canSave = input.required<boolean>();
  isSaving = input.required<boolean>();
  disabled = input<boolean>(false);
  twoStage = input<boolean>(false);
  showImportanceScore = input<boolean>(true);
  contentTestId = input<string>();
  attributesListTestId = input<string>();

  attributeValueChange = output<AttributeValueChange>();
  commentChange = output<AttributeValueChange>();
  saveClick = output<void>();

  private readonly expandedKeys = signal<ReadonlySet<string>>(new Set());

  protected readonly canSaveAttributes = computed(
    () =>
      this.canSave() &&
      this.attributes().every((attr) => !attr.hasChanges || attr.score != null || attr.scoreAbsent),
  );
  protected readonly reviewEnabled = computed(() => this.suggestionStore.enabled());
  protected readonly visibleBadgeListItems = computed(() =>
    this.badgeListItems().filter((item) => this.suggestionStore.matchesFilter(item.originalItem)),
  );
  protected readonly showsFilterEmptyHint = computed(
    () => this.suggestionStore.filterActive() && this.visibleBadgeListItems().length === 0,
  );

  protected attributeScoreChanged(param: AttributeValueChange) {
    this.attributeValueChange.emit(param);
  }

  protected onItemToggle(toggle: BadgeListItemToggle): void {
    this.expandedKeys.update((keys) => {
      const updated = new Set(keys);
      if (toggle.collapsed) {
        updated.delete(toggle.key);
      } else {
        updated.add(toggle.key);
      }
      return updated;
    });
  }

  protected isExpanded(key: string): boolean {
    return this.expandedKeys().has(key);
  }
}
